import React, { FC, useMemo } from 'react'

import { useModal } from '@context/ModalsContext'

import { Button } from '@ui/components/Button'
import { Chip } from '@ui/components/Chip'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { IconFCType } from '@ui/icons/types'

import { Operator } from '@utils/ApiClient'
import { extractValueFromFilterQuery } from '@utils/extractValueFromFilterQuery'

import { FilterQueryProps } from '@model/FilterFields'

import { CheckBoxProps, RangeModal } from './components/Range.modal'
import { FromTo } from './interfaces/fromTo.interface'

interface FilterChipModalRangeProps {
  placeholders?: FromTo
  name: string
  keys: FromTo
  state?: FilterQueryProps
  isQueryParameter?: boolean
  checkBox?: CheckBoxProps
  onSelect?: (value: FilterQueryProps) => void
  inputIcon?: IconFCType
  size?: 'sm' | 'md' | 'lg'
}

export const FilterChipModalRange: FC<FilterChipModalRangeProps> = (props) => {
  const rangeModal = useModal(RangeModal)

  const {
    placeholders,
    name,
    keys,
    state,
    isQueryParameter,
    checkBox,
    onSelect,
    inputIcon: InputIcon,
    size = 'md',
  } = props

  const countText = useMemo<string>(() => {
    let count = 0
    if (state?.[keys.from]) count++
    if (state?.[keys.to]) count++
    if (checkBox && state?.[checkBox.key]) count++
    return count > 0 ? String(count) : ''
  }, [checkBox, keys.from, keys.to, state])

  const getValueBasedOnQueryParameter = (value: string, operator: Operator) => {
    if (!value?.length) return undefined
    return isQueryParameter ? value : { value, operator }
  }

  const handleApply = (values: FilterQueryProps) => {
    onSelect?.({
      ...state,
      [keys.from]: getValueBasedOnQueryParameter(
        values[keys.from] as string,
        Operator.MORE_OR_EQUAL
      ),
      [keys.to]: getValueBasedOnQueryParameter(values[keys.to] as string, Operator.LESS_OR_EQUAL),
      ...(checkBox ? { [checkBox.key]: values[checkBox.key] } : {}),
    })
    rangeModal.close()
  }

  const initialValues: FilterQueryProps = {
    [keys.from]: extractValueFromFilterQuery(state?.[keys.from]) ?? '',
    [keys.to]: extractValueFromFilterQuery(state?.[keys.to]) ?? '',
    ...(checkBox ? { [checkBox.key]: state?.[checkBox.key] } : {}),
  }

  const clearChip = (event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    onSelect?.({
      ...state,
      [keys.from]: undefined,
      [keys.to]: undefined,
      ...(checkBox ? { [checkBox.key]: undefined } : {}),
    })
  }

  return (
    <Button.Secondary
      iconRight={ChevronIcon.Down}
      size={size}
      onClick={() =>
        rangeModal.open({
          name,
          keys,
          placeholders,
          checkBox,
          InputIcon,
          initialValues,
          onApply: handleApply,
        })
      }
      endDecorator={
        (state?.[keys.from] || state?.[keys.to] || (checkBox && state?.[checkBox.key])) && (
          <Chip selected size='xs' onDelete={clearChip} key={`${keys.from}-${keys.to}`}>
            {countText}
          </Chip>
        )
      }
    >
      {name}
    </Button.Secondary>
  )
}
